import React, { useEffect, useState } from 'react';

import { Home } from '@mui/icons-material';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getBenefitDropdownOptions,
  getDmsContractActivity,
  getEdgeContractActivity,
  saveContractActivity,
} from './operations';
import {
  MAX_BENEFIT_TERMINATION_DATE,
  PAY_RATE_UNITS,
  PAY_TYPES,
  PORTAL_STATUS,
  PORTAL_STATUS_LABELS,
  benefitTimeStatusFor,
} from './utils';
import {
  Breadcrumbs,
  Button,
  CheckboxRow,
  FormContainer,
  FormDateField,
  FormRadioField,
  FormSelectField,
  FormTextField,
  Footer,
  InfoTextField,
  ModalConfirmation,
  SubHeader,
} from '../../components';
import { formatIsoToDate } from '../../utils';

import './styles.scoped.css';

const moneyValue = (raw, max) => {
  const value = raw.replace(/[^0-9.]/g, '');
  if (value === '') return '';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '';
  return Math.min(amount, max);
};

const ContractActivityDetails = () => {
  const navigate = useNavigate();
  const { activityId } = useParams();
  const [activity, setActivity] = useState(null);
  const [form, setForm] = useState({});
  const [dropdowns, setDropdowns] = useState({ classCodes: [], divisionCodes: [], planTypes: [], typesOfCoverage: [] });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [confirmModal, setConfirmModal] = useState(null); // 'missingRequired' | 'termDate'
  const [medicalLookupLoading, setMedicalLookupLoading] = useState(false);

  useEffect(() => {
    Promise.all([getDmsContractActivity(activityId), getBenefitDropdownOptions()])
      .then(([activityResponse, options]) => {
        if (!activityResponse) {
          setLoadError('Contract activity not found.');
          return;
        }
        setActivity(activityResponse);
        setDropdowns(options);
        setForm({
          portalStatus: activityResponse.portalStatus || PORTAL_STATUS.HIRE_DNS,
          forPayroll: !!activityResponse.forPayroll,
          clearServiceActionFlag: !!activityResponse.clearServiceActionFlag,
          clearBenefitActionFlag: !!activityResponse.clearBenefitActionFlag,
          payRate: activityResponse.payRate ?? '',
          payRateUnit: activityResponse.payRateUnit || '',
          payType: activityResponse.payType || '',
          salaryAmount: activityResponse.salaryAmount ?? '',
          fullOrPartTime: activityResponse.fullOrPartTime || '',
          benefitsElection: activityResponse.benefitsElection || '',
          notes: activityResponse.notes || '',
          benefitEffectiveDate: activityResponse.benefitEffectiveDate || '',
          benefitTerminationDate: activityResponse.benefitTerminationDate || MAX_BENEFIT_TERMINATION_DATE,
          medPolicy: activityResponse.medPolicy || '',
          typeOfCoverage: activityResponse.typeOfCoverage || '',
          planType: activityResponse.planType || '',
          benefitClassCode: activityResponse.benefitClassCode || '',
          benefitDivisionCode: activityResponse.benefitDivisionCode || '',
          billingCycle: activityResponse.billingCycle || '',
          billingAmount: activityResponse.billingAmount ?? '',
          medicalContractActivityId: activityResponse.medicalContractActivityId || '',
          medicalJobOrderId: activityResponse.medicalJobOrderId ?? null,
          medicalContractStartDate: activityResponse.medicalContractStartDate || null,
          medicalContractEndDate: activityResponse.medicalContractEndDate || null,
          medicalFillDate: activityResponse.medicalFillDate || null,
          medicalFillPc: activityResponse.medicalFillPc || null,
        });
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [activityId]);

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setSaveError('');
  };

  // Pay Rate unit drives Pay Type (UC 5.2): Per Hour -> Hourly; Per Week -> Salary
  // (rate x 52); Per Year -> Salary (rate as entered).
  const handlePayRateUnitChange = unit => {
    setForm(prev => {
      const next = { ...prev, payRateUnit: unit };
      const rate = Number(prev.payRate) || 0;
      if (unit === 'PER_HOUR') {
        next.payType = PAY_TYPES.HOURLY;
        next.salaryAmount = '';
      } else if (unit === 'PER_WEEK') {
        next.payType = PAY_TYPES.SALARY;
        next.salaryAmount = rate ? Math.min(rate * 52, 999999.99) : '';
      } else if (unit === 'PER_YEAR') {
        next.payType = PAY_TYPES.SALARY;
        next.salaryAmount = rate || '';
      }
      return next;
    });
    setErrors(prev => ({ ...prev, payRateUnit: undefined, payType: undefined }));
  };

  // Medical Contract Activity ID: six digits, then auto-populate from Edge (UC 5.3)
  const handleMedicalIdChange = raw => {
    const value = raw.replace(/\D/g, '').slice(0, 6);
    setField('medicalContractActivityId', value);
    if (value.length === 6) {
      setMedicalLookupLoading(true);
      getEdgeContractActivity(value)
        .then(edge =>
          setForm(prev => ({
            ...prev,
            medicalJobOrderId: edge.jobOrderId ?? null,
            medicalContractStartDate: edge.contractStartDate ?? null,
            medicalContractEndDate: edge.contractEndDate ?? null,
            medicalFillDate: edge.fillDate ?? null,
            medicalFillPc: edge.fillPc ?? null,
          })),
        )
        .catch(() =>
          setErrors(prev => ({ ...prev, medicalContractActivityId: 'Contract activity not found in Edge.' })),
        )
        .finally(() => setMedicalLookupLoading(false));
    }
  };

  const missingRequiredFields = () => {
    const missing = {};
    if (!(Number(form.payRate) > 0)) missing.payRate = 'Pay Rate is required.';
    if (!form.payRateUnit) missing.payRateUnit = 'Select what the amount is.';
    if (!form.payType) missing.payType = 'Pay Type is required.';
    if (form.payType === PAY_TYPES.SALARY && !(Number(form.salaryAmount) > 0)) {
      missing.salaryAmount = 'Salary amount is required.';
    }
    if (!form.benefitEffectiveDate) missing.benefitEffectiveDate = 'Benefit Effective Date is required.';
    if (!form.benefitClassCode) missing.benefitClassCode = 'Benefit Class Code is required.';
    if (!form.benefitDivisionCode) missing.benefitDivisionCode = 'Benefit Division Code is required.';
    return missing;
  };

  const persist = async () => {
    setSaving(true);
    setSaveError('');
    const sendToBenefits =
      form.portalStatus === PORTAL_STATUS.HIRE_SEND || form.portalStatus === PORTAL_STATUS.TERM_SEND;
    try {
      await saveContractActivity({
        id: activity.id,
        portalStatus: form.portalStatus,
        forPayroll: form.forPayroll,
        clearServiceActionFlag: form.clearServiceActionFlag,
        // Choosing a Send status re-raises the Benefits Action badge (UC 5.1/5.3)
        clearBenefitActionFlag: sendToBenefits ? false : form.clearBenefitActionFlag,
        payRate: form.payRate === '' ? null : Number(form.payRate),
        payRateUnit: form.payRateUnit || null,
        payType: form.payType || null,
        salaryAmount: form.salaryAmount === '' ? null : Number(form.salaryAmount),
        fullOrPartTime: form.fullOrPartTime || null,
        benefitsElection: form.benefitsElection || null,
        notes: form.notes || null,
        benefitEffectiveDate: form.benefitEffectiveDate || null,
        benefitTerminationDate: form.benefitTerminationDate || null,
        benefitTimeStatus: benefitTimeStatusFor(form.payType) || null,
        medPolicy: form.medPolicy || null,
        typeOfCoverage: form.typeOfCoverage || null,
        planType: form.planType || null,
        benefitClassCode: form.benefitClassCode || null,
        benefitDivisionCode: form.benefitDivisionCode || null,
        billingCycle: form.billingCycle || null,
        billingAmount: form.billingAmount === '' ? null : Number(form.billingAmount),
        medicalContractActivityId: form.medicalContractActivityId || null,
        medicalJobOrderId: form.medicalJobOrderId,
        medicalContractStartDate: form.medicalContractStartDate,
        medicalContractEndDate: form.medicalContractEndDate,
        medicalFillDate: form.medicalFillDate,
        medicalFillPc: form.medicalFillPc,
      });
      navigate(-1);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Saving depends on the Portal Status (UC 5.3 Save and Cancel Section)
  const handleSave = () => {
    if (form.portalStatus === PORTAL_STATUS.HIRE_SEND) {
      const missing = missingRequiredFields();
      if (Object.keys(missing).length) {
        setErrors(prev => ({ ...prev, ...missing }));
        setConfirmModal('missingRequired');
        return;
      }
    }
    if (
      form.portalStatus === PORTAL_STATUS.TERM_SEND &&
      (!form.benefitTerminationDate ||
        form.benefitTerminationDate === MAX_BENEFIT_TERMINATION_DATE ||
        (activity.contractStartDate && form.benefitTerminationDate <= activity.contractStartDate))
    ) {
      setConfirmModal('termDate');
      return;
    }
    persist();
  };

  const benefitTimeStatus = benefitTimeStatusFor(form.payType);

  if (loading) {
    return (
      <>
        <SubHeader
          text="DMS Driver Log"
          buttons={[{ icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        />
        <div className="wrap">
          <Spinner animation="border" style={{ margin: '40px auto', color: 'var(--primary)' }} />
        </div>
        <Footer />
      </>
    );
  }

  if (loadError || !activity) {
    return (
      <>
        <SubHeader
          text="DMS Driver Log"
          buttons={[{ icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        />
        <div className="wrap">
          <div className="field-error">{loadError || 'Contract activity not found.'}</div>
        </div>
        <Footer />
      </>
    );
  }

  const driverTitle = [activity.driverName, activity.driverId].filter(Boolean).join(' - ');

  return (
    <>
      <SubHeader
        text="DMS Driver Log"
        buttons={[{ icon: <Home sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
      />
      <Breadcrumbs
        leftTitle="DMS Driver Log"
        middleTitle={driverTitle}
        midItemAction={() => navigate(`/dms-driver-log/driver/${activity.driverId}`)}
        rightTitle="Details"
        route="/dms-driver-log"
      />
      <FormContainer title={`Contract Activity ${activity.id}`}>
        {/* 5.1 Header Details */}
        <div className="details-row">
          <div className="details-section details-main">
            <InfoTextField label="Driver Name & Number" required value={driverTitle} />
            <InfoTextField label="LOB" value={activity.lob} />
            <InfoTextField label="Branch" value={activity.branch} />
            <InfoTextField label="Recruiter RPC" value={activity.recruiterRpc} />
            <InfoTextField label="Entered Date" value={formatIsoToDate(activity.enteredDate)} />
            <InfoTextField label="Contract Activity ID" value={activity.id} />
            <InfoTextField label="Contract Start Date" value={formatIsoToDate(activity.contractStartDate)} />
            <InfoTextField label="Contract End Date" value={formatIsoToDate(activity.contractEndDate)} />
            <InfoTextField label="Job Order ID" value={activity.jobOrderId} />
            <InfoTextField label="Client ID" value={activity.clientId} />
            <InfoTextField label="Client Name" value={activity.clientName} />
          </div>
          <div className="details-col">
            <div className="status-box">
              <FormRadioField
                name="portalStatus"
                options={Object.values(PORTAL_STATUS).map(value => ({
                  label: PORTAL_STATUS_LABELS[value],
                  value,
                }))}
                value={form.portalStatus}
                onChange={event => setField('portalStatus', event.target.value)}
              />
            </div>
            <CheckboxRow
              label="Clear Service Action Flag"
              checked={form.clearServiceActionFlag}
              onChange={event => setField('clearServiceActionFlag', event.target.checked)}
            />
            <InfoTextField label="Sent to BSwift Start" value={formatIsoToDate(activity.startBenSentDate)} />
            <InfoTextField label="Sent to BSwift Term" value={formatIsoToDate(activity.termBenSentDate)} />
          </div>
        </div>
        <CheckboxRow
          label="Contract Activity is for payroll"
          checked={form.forPayroll}
          onChange={event => setField('forPayroll', event.target.checked)}
        />
        <div className="section-divider" />
        {/* 5.2 Activity Additional Info */}
        <div className="details-row">
          <div className="details-col">
            <FormTextField
              label="Pay Rate"
              required
              type="text"
              inputMode="decimal"
              value={form.payRate}
              onChange={event => setField('payRate', moneyValue(event.target.value, 999999.99))}
              error={!!errors.payRate}
              errorMessage={errors.payRate}
            />
            <FormRadioField
              name="payRateUnit"
              options={PAY_RATE_UNITS}
              value={form.payRateUnit}
              onChange={event => handlePayRateUnitChange(event.target.value)}
              error={!!errors.payRateUnit}
              errorMessage={errors.payRateUnit}
            />
          </div>
          <div className="details-col">
            <FormRadioField
              label="Pay Type"
              required
              name="payType"
              options={[
                { label: 'Hourly', value: PAY_TYPES.HOURLY },
                { label: 'Salary', value: PAY_TYPES.SALARY },
                { label: 'Other (See Notes)', value: PAY_TYPES.OTHER },
              ]}
              value={form.payType}
              onChange={event => setField('payType', event.target.value)}
              error={!!errors.payType}
              errorMessage={errors.payType}
            />
            <FormTextField
              label="Salary"
              type="text"
              inputMode="decimal"
              disabled={form.payType !== PAY_TYPES.SALARY}
              value={form.salaryAmount}
              onChange={event => setField('salaryAmount', moneyValue(event.target.value, 999999.99))}
              error={!!errors.salaryAmount}
              errorMessage={errors.salaryAmount}
            />
          </div>
          <div className="details-col">
            <FormRadioField
              label="Fulltime or Part Time"
              name="fullOrPartTime"
              options={[
                { label: 'FT', value: 'FT' },
                { label: 'PT', value: 'PT' },
              ]}
              value={form.fullOrPartTime}
              onChange={event => setField('fullOrPartTime', event.target.value)}
            />
            <FormRadioField
              label="Benefits"
              name="benefitsElection"
              options={[
                { label: 'With Benefits', value: 'WITH_BENEFITS' },
                { label: 'No Benefits', value: 'NO_BENEFITS' },
              ]}
              value={form.benefitsElection}
              onChange={event => setField('benefitsElection', event.target.value)}
            />
          </div>
          <div className="details-col">
            <label className="notes-label" htmlFor="dms-notes">
              Notes
            </label>
            <textarea
              id="dms-notes"
              className="notes-textarea"
              maxLength={2000}
              value={form.notes}
              onChange={event => setField('notes', event.target.value)}
            />
          </div>
        </div>
        {/* 5.3 Benefit Section */}
        <div className="benefit-box">
          <div className="details-row">
            <div className="details-col">
              <FormDateField
                label="Benefit Effective Date"
                required
                value={form.benefitEffectiveDate}
                onChange={event => setField('benefitEffectiveDate', event.target.value)}
                error={!!errors.benefitEffectiveDate}
                errorMessage={errors.benefitEffectiveDate}
              />
              <FormDateField
                label="Benefit Termination Date"
                value={form.benefitTerminationDate}
                onChange={event => setField('benefitTerminationDate', event.target.value)}
                error={!!errors.benefitTerminationDate}
                errorMessage={errors.benefitTerminationDate}
              />
              <InfoTextField label="Benefit Time Status" required value={benefitTimeStatus} />
              <FormRadioField
                label="Med Kaiser MEK / Med Blue Shield MEB"
                name="medPolicy"
                options={[
                  { label: 'MEK', value: 'MEK' },
                  { label: 'MEB', value: 'MEB' },
                ]}
                value={form.medPolicy}
                onChange={event => setField('medPolicy', event.target.value)}
              />
            </div>
            <div className="details-col">
              <FormSelectField
                label="Type of Coverage"
                options={dropdowns.typesOfCoverage}
                value={dropdowns.typesOfCoverage.find(option => option.value === form.typeOfCoverage) || null}
                onChange={option => setField('typeOfCoverage', option?.value || '')}
              />
              <FormSelectField
                label="Plan Type"
                options={dropdowns.planTypes}
                value={dropdowns.planTypes.find(option => option.value === form.planType) || null}
                onChange={option => setField('planType', option?.value || '')}
              />
              <FormSelectField
                label="Benefit Class Code"
                required
                options={dropdowns.classCodes}
                value={dropdowns.classCodes.find(option => option.value === form.benefitClassCode) || null}
                onChange={option => setField('benefitClassCode', option?.value || '')}
                error={!!errors.benefitClassCode}
                errorMessage={errors.benefitClassCode}
              />
              <FormSelectField
                label="Benefit Division Code"
                required
                options={dropdowns.divisionCodes}
                value={dropdowns.divisionCodes.find(option => option.value === form.benefitDivisionCode) || null}
                onChange={option => setField('benefitDivisionCode', option?.value || '')}
                error={!!errors.benefitDivisionCode}
                errorMessage={errors.benefitDivisionCode}
              />
            </div>
          </div>
          <div className="section-divider" />
          <div className="details-row">
            <div className="details-col">
              <FormRadioField
                label="Billing"
                name="billingCycle"
                options={[
                  { label: 'Weekly', value: 'WEEKLY' },
                  { label: 'Monthly', value: 'MONTHLY' },
                ]}
                value={form.billingCycle}
                onChange={event => setField('billingCycle', event.target.value)}
              />
              <FormTextField
                label="Billing Amount"
                type="text"
                inputMode="decimal"
                value={form.billingAmount}
                onChange={event => setField('billingAmount', moneyValue(event.target.value, 9999.99))}
              />
              <CheckboxRow
                label="Clear Benefit Action Flag"
                checked={form.clearBenefitActionFlag}
                onChange={event => setField('clearBenefitActionFlag', event.target.checked)}
              />
            </div>
            <div className="details-col">
              <FormTextField
                label="Medical Contract Activity ID"
                type="text"
                inputMode="numeric"
                loading={medicalLookupLoading}
                value={form.medicalContractActivityId}
                onChange={event => handleMedicalIdChange(event.target.value)}
                error={!!errors.medicalContractActivityId}
                errorMessage={errors.medicalContractActivityId}
              />
              <InfoTextField label="Job Order ID" value={form.medicalJobOrderId} />
              <InfoTextField label="Contract Start Date" value={formatIsoToDate(form.medicalContractStartDate)} />
              <InfoTextField label="Contract End Date" value={formatIsoToDate(form.medicalContractEndDate)} />
              <InfoTextField label="Fill Date" value={formatIsoToDate(form.medicalFillDate)} />
              <InfoTextField label="Fill PC" value={form.medicalFillPc} />
            </div>
          </div>
        </div>
        {saveError && <div className="field-error">{saveError}</div>}
        <div className="save-cancel-row">
          <Button variant="primary" text={saving ? 'SAVING...' : 'SAVE'} onPress={handleSave} disabled={saving} />
          <Button variant="outline" text="CANCEL" onPress={() => navigate(-1)} disabled={saving} />
        </div>
      </FormContainer>
      <ModalConfirmation
        title="Please Confirm"
        body="Mandatory fields (red asterisks) are missing data, save anyway and close?"
        isOpen={confirmModal === 'missingRequired'}
        primaryButton
        primaryButtonTitle="YES"
        onClick={() => {
          setConfirmModal(null);
          persist();
        }}
        optionalClose
        secondaryButtonTitle="CANCEL"
        secondaryOnClick={() => setConfirmModal(null)}
        isLoading={saving}
      />
      <ModalConfirmation
        title="Please Confirm"
        body="To send termination data, Benefit Termination Date should be updated."
        isOpen={confirmModal === 'termDate'}
        primaryButton
        primaryButtonTitle="OK"
        onClick={() => {
          setConfirmModal(null);
          persist();
        }}
        optionalClose
        secondaryButtonTitle="CANCEL"
        secondaryOnClick={() => setConfirmModal(null)}
        isLoading={saving}
      />
      <Footer />
    </>
  );
};

export default ContractActivityDetails;
